/**
 * downloadAttachment.ts — R17 (2026-08-14 owner report: "I can't even download it").
 *
 * Every download surface used a bare `<a download>` pointed at the CROSS-ORIGIN
 * signed URL. Browsers ignore the download attribute on cross-origin hrefs, so
 * for types they can render — HTML above all — the click NAVIGATED to the raw
 * file instead of saving it (for a broken build: the black page again, nothing
 * saved). PDFs/DOCX masked the bug for months because browsers download what
 * they cannot render inline.
 *
 * Fix: bytes-first. Reuse the preview's two-stage content ladder (direct fetch
 * → authenticated server resolver; signed GCS URLs serve no CORS headers, so
 * the resolver is the reliable half) and save through a Blob object URL, which
 * every browser honors regardless of origin. Raw-anchor navigation survives
 * only as the very last resort for types the browser downloads natively.
 */
import {
  canResolveServerSide,
  resolveAttachmentContent,
  type AttachmentResolveContext,
} from './resolveAttachment';
import type { SerializedAttachment } from '../GlobalChatSystem/types';

/** Save a Blob through an object-URL anchor — the one download path browsers
 *  honor unconditionally. */
export function saveBlobAs(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke off the click path — immediate revocation races the save in Safari.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

/** Last-resort legacy behavior: navigation. Correct only for types the
 *  browser cannot render (it downloads them); renderable types would open
 *  instead of saving — which is exactly the bug this module exists to fix —
 *  so callers reach here only after every bytes route failed. */
function legacyAnchorNavigate(href: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = fileName;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Download an attachment as an actual saved file. Returns true when a save
 * was initiated through the Blob path, false when it fell to legacy
 * navigation (or nothing was possible).
 */
export async function downloadAttachmentAsFile(params: {
  url?: string | null;
  fileName: string;
  declaredType?: string | null;
  serialized?: SerializedAttachment | null;
  context?: AttachmentResolveContext | null;
}): Promise<boolean> {
  const { url, fileName } = params;
  const fallbackType = params.declaredType || 'application/octet-stream';
  if (url) {
    try {
      const resp = await fetch(url);
      if (resp.ok) {
        const blob = await resp.blob();
        saveBlobAs(blob.type ? blob : new Blob([blob], { type: fallbackType }), fileName);
        return true;
      }
    } catch {
      /* CORS/network — the server resolver is the designed fallback */
    }
  }
  if (canResolveServerSide(params.serialized, params.context)) {
    try {
      const resolved = await resolveAttachmentContent(params.serialized!, params.context);
      saveBlobAs(
        new Blob([resolved.bytes], { type: resolved.contentType || fallbackType }),
        fileName,
      );
      return true;
    } catch {
      /* fall through to legacy navigation */
    }
  }
  if (url) {
    legacyAnchorNavigate(url, fileName);
  }
  return false;
}
